import styled from '@emotion/styled';
import { MenuList, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';

import { useEffect, useMemo, useRef } from 'react';
import eventEmitter, { IFileDownloadProps } from 'src/Shared/Utilities/eventEmitter';
// import SearchBar from 'src/feature/SearchBar/SearchBar';
import { CloudDismissRegular } from '@fluentui/react-icons';
import { Button, red } from '@galaxy-react/core';
import axios from 'axios';
import { useLiveQuery } from 'dexie-react-hooks';
import { t } from 'i18next';
import {
  AsyncError,
  AsyncErrorCodeEnum,
  backOffTimeCalculation,
  getResponseAndContentType,
  timeout,
} from 'src/feature/Reports/Hooks/models';
import Count from 'src/icons/count.svg?react';
import { db, FileItem } from 'src/services/db';
import LRToolTip from 'src/Shared/Components/Tooltip';
import Typography from 'src/Shared/Components/Typography';
import { getCurrentISODate } from 'src/Shared/Utilities/DateFormatter';
import { downloadFilesOnBrowser } from 'src/Shared/Utilities/download';
import DownloadItem from './DownloadItem';

const CardWrap = styled('div')({
  minWidth: '272px',
  backgroundColor: '#fff',
  maxHeight: 400,
});

type Props = {};
const toasterId = 'downloadPanel';
export async function loopForReport(pollUrl: string, format): Promise<any> {
  try {
    const type = getResponseAndContentType(format);
    return axios.get(pollUrl, {
      responseType: type.responseType,
      headers: { 'Content-Type': type.contentType },
    });
  } catch (e) {
    throw new AsyncError('fail to get report', AsyncErrorCodeEnum.FailToGetReport);
  }
}
const downloadFiles = async (id, format, pollUrl) => {
  if (id) {
    const addCancelMarker = (downloadId: string) => {
      try {
        const key = 'downloadCancelIds';
        const raw = localStorage.getItem(key);
        const ids: string[] = raw ? JSON.parse(raw) : [];
        if (!ids.includes(downloadId)) {
          ids.push(downloadId);
          localStorage.setItem(key, JSON.stringify(ids));
        }
      } catch (_) {
        return;
      }
    };
    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t('There is a download task in progress, are you sure you want to leave?');
      return e.returnValue;
    };
    // wait for 3s, backend needs 3s at least to generate the file
    const sendCancelToSW = (downloadId: string) => {
      const payload = { type: 'cancel-download', id: downloadId } as any;
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage(payload);
      } else if (navigator.serviceWorker) {
        navigator.serviceWorker.ready.then((reg) => reg.active?.postMessage(payload)).catch(() => {});
      }
    };
    const handleUnload = async (e: PageTransitionEvent) => {
      sendCancelToSW(id);
      addCancelMarker(id);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleUnload);

    await timeout(3000);
    let timeConsumed = 3000;

    try {
      for (let i = 0; i < 50; i++) {
        const response = await loopForReport(`${pollUrl}/${id}`, format);
        if (response.status === 202) {
          console.log('request count:', i + 1, ', time consumed:', timeConsumed / 1000, 'seconds');
          if ([20, 26].includes(i) || (i >= 31 && (i - 31) % 3 === 0)) {
            // if the request is not v2, show "The export is still processing and will be ready soon." only
            eventEmitter.emit('toast', {
              title: t('The export is still processing and will be ready soon.'),
              type: 'info',
              message: pollUrl.includes('v2')
                ? t('Exporting as CSV is recommended for quicker report generation.')
                : '',
              duration: 10000,
            });
          }
          await timeout(backOffTimeCalculation(i));
          timeConsumed += backOffTimeCalculation(i);
          continue;
        }
        if (response.status === 303) {
          let payload: any = response.data as any;
          try {
            if (payload instanceof Blob) {
              const text = await payload.text();
              payload = JSON.parse(text);
            } else if (typeof payload === 'string') {
              payload = JSON.parse(payload);
            }
          } catch (_) {
            console.log('parse error', _);
          }
          const presignedURL: string | undefined = payload?.presignedURL;
          if (presignedURL) {
            window.open(presignedURL, '_blank');
          }
          db.downloadList.update(id, {
            status: 'DONE',
          });
          return;
        }
        if (response.status === 413) {
          eventEmitter.emit('toast', {
            title: t('Unable to generate report as data limit has exceeded.'),
            message: t('Please refine filters and try again.'),
            type: 'error',
            duration: 6000,
          });
          db.downloadList.update(id, {
            status: 'DONE',
          });
          return;
        }
        if (response.status === 200) {
          downloadFilesOnBrowser(response, format);
          const reportInfo = await db.downloadList.get(id);
          db.downloadList.update(id, {
            status: 'DONE',
          });
          eventEmitter.emit('toast', {
            title: t('Download Successful'),
            message: reportInfo.title,
            type: 'success',
            duration: 6000,
          });
          return;
        } else if (response.status === 204) {
          const reportInfo = await db.downloadList.get(id);
          eventEmitter.emit('toast', {
            title: t('No records found'),
            message: reportInfo.title,
            type: 'info',
            duration: 6000,
          });
          db.downloadList.update(id, {
            status: 'DONE',
          });
          return;
        } else {
          db.downloadList.update(id, {
            status: 'ERROR',
          });
          throw new AsyncError('Unknown Error', AsyncErrorCodeEnum.Unknown);
        }
      }
      eventEmitter.emit('toast', {
        title: t('TIME OUT'),
        message: t('Please refine filters and try again.'),
        type: 'error',
        duration: 6000,
      });
      throw new AsyncError('TIME OUT', AsyncErrorCodeEnum.TooManyRequestsSent);
    } catch (e: any) {
      db.downloadList.update(id, {
        status: 'ERROR',
      });
    } finally {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleUnload);
    }
  }
};
const handleDownload = (item: IFileDownloadProps) => {
  // dismissAllToasts();
  // notify();
  db.downloadList.add({
    id: item.id,
    title: item.title,
    createdAt: getCurrentISODate(),
    format: item.config.fileFormat,
    status: 'PENDING',
  });
  const pollUrl = item.config.getResultUrl;
  downloadFiles(item.id, item.config.fileFormat, pollUrl);
};

const clearAll = (list: FileItem[]) => {
  list.forEach((item) => {
    db.downloadList.delete(item.id);
  });
};
const DownloadPanel = (props: Props) => {
  const downloadList = useLiveQuery(() => db.downloadList.orderBy('createdAt').reverse().toArray());
  const ref = useRef<HTMLDivElement>(null);
  const downloadingList = useMemo(() => {
    return downloadList?.filter((item) => item.status === 'PENDING');
  }, [downloadList]);
  const downloadErrorList = useMemo(() => {
    return downloadList?.filter((item) => item.status === 'CANCELLED' || item.status === 'ERROR');
  }, [downloadList]);

  useEffect(() => {
    eventEmitter.on('fileDownload', handleDownload);
    return () => {
      eventEmitter.off('fileDownload', handleDownload);
    };
  }, [downloadList]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!!downloadErrorList?.length && (
        <Popover withArrow>
          <LRToolTip
            content={t(`${downloadErrorList?.length} file${downloadErrorList?.length > 1 ? 's' : ''} downloads failed`)}
            relationship="description"
          >
            <PopoverTrigger>
              <CloudDismissRegular
                style={{ fontSize: 32, verticalAlign: 'middle', cursor: 'pointer', color: red[500] }}
              ></CloudDismissRegular>
            </PopoverTrigger>
          </LRToolTip>
          <PopoverSurface tabIndex={-1} style={{ padding: 0 }}>
            <CardWrap style={{ background: '#fff', maxHeight: 500 }}>
              <MenuList style={{ overflowY: 'auto', maxHeight: 400 }}>
                {downloadErrorList?.map((item) => {
                  return <DownloadItem key={item.id} {...item}></DownloadItem>;
                })}
              </MenuList>
              <Button onClick={() => clearAll(downloadErrorList)} appearance="transparent" style={{ width: '100%' }}>
                {t('Clear all')}
              </Button>
            </CardWrap>
          </PopoverSurface>
        </Popover>
      )}
      {!!downloadingList?.length && (
        <LRToolTip
          withArrow
          defaultVisible
          content={
            downloadingList?.length > 1
              ? t(`${downloadingList?.length} files are generating`)
              : t('1 file is generating')
          }
          relationship="description"
          positioning="below"
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            ref={ref}
          >
            <Typography style={{ fontSize: 14, position: 'absolute', color: '#E5EDF3', fontWeight: 'bold' }}>
              {downloadingList?.length > 99 ? '99+' : downloadingList?.length}
            </Typography>
            <Count style={{ height: '40px', width: '40px', color: '#E5EDF3' }}></Count>
          </div>
        </LRToolTip>
      )}
    </div>
  );
};

export default DownloadPanel;
